import { COST_GROUPINGS, type CostQuery, type CostResult, type CostSeries } from './model';

/** One raw cost line: what Cost Explorer would return at daily granularity with every dimension attached. */
export interface CostRow {
  date: string;
  account: string;
  service: string;
  usage_type: string;
  tenant: string;
  region: string;
  amount: number;
}

const ACCOUNTS: { key: string; share: number }[] = [
  { key: 'openr-dev', share: 0.36 },
  { key: 'openr-staging', share: 0.17 },
  { key: 'openr-prod', share: 0.3 },
  { key: 'openr-transit', share: 0.08 },
  { key: 'openr (mgmt)', share: 0.09 },
];

const SERVICES: { key: string; share: number; usage: string[]; region: string }[] = [
  {
    key: 'Amazon RDS',
    share: 0.27,
    usage: ['InstanceUsage:db.m7g.large', 'Multi-AZUsage', 'StorageUsage'],
    region: 'eu-west-1',
  },
  {
    key: 'Amazon EKS',
    share: 0.19,
    usage: ['ExtendedSupport', 'AmazonEKS-Hours'],
    region: 'eu-west-1',
  },
  {
    key: 'Amazon EC2',
    share: 0.14,
    usage: ['BoxUsage:t3.medium', 'EBS:VolumeUsage.gp3', 'NatGateway-Hours', 'NatGateway-Bytes'],
    region: 'eu-west-1',
  },
  {
    key: 'AWS Lambda',
    share: 0.09,
    usage: ['Request', 'Lambda-GB-Second', 'Lambda-Provisioned-GB-Second'],
    region: 'eu-west-1',
  },
  {
    key: 'Amazon VPC',
    share: 0.08,
    usage: ['VpnConnection-Hours', 'TransitGateway-Hours', 'VPC-Encryption-Controls'],
    region: 'eu-west-1',
  },
  {
    key: 'Amazon S3',
    share: 0.06,
    usage: ['TimedStorage-ByteHrs', 'Requests-Tier1', 'DataTransfer-Out-Bytes'],
    region: 'eu-west-1',
  },
  {
    key: 'Amazon CloudFront',
    share: 0.05,
    usage: ['Requests-HTTPS', 'DataTransfer-Out-Bytes'],
    region: 'global',
  },
  {
    key: 'Amazon CloudWatch',
    share: 0.05,
    usage: ['DataProcessing-Bytes', 'MetricMonitorUsage', 'Lambda Insights'],
    region: 'eu-west-1',
  },
  {
    key: 'AWS Secrets Manager',
    share: 0.03,
    usage: ['Secret-Month', 'APIRequest'],
    region: 'eu-west-1',
  },
  {
    key: 'AWS Billing Conductor',
    share: 0.01,
    usage: ['USE1-Proforma-Accounts'],
    region: 'us-east-1',
  },
  {
    key: 'API Gateway',
    share: 0.03,
    usage: ['ApiGatewayRequest', 'ApiGatewayCache'],
    region: 'eu-west-1',
  },
];

const TENANTS = ['azzurri', 'burgerking', 'hallandwoodhouse', 'bigtablegroup', 'stonegate'];

/** Audited monthly totals for 2026, USD; months after September are projected flat. */
const MONTH_TOTALS: Record<string, number> = {
  '2025-11': 6100,
  '2025-12': 6050,
  '2026-01': 5990,
  '2026-02': 6020,
  '2026-03': 6180,
  '2026-04': 5987,
  '2026-05': 6613,
  '2026-06': 5942,
  '2026-07': 6128,
  '2026-08': 6357,
  '2026-09': 4680,
  '2026-10': 4590,
};

const seeded = (seed: number) => {
  let value = seed % 2147483647;
  return () => {
    value = (value * 48271) % 2147483647;
    return value / 2147483647;
  };
};

let cache: CostRow[] | null = null;

/** Deterministic daily rows across a year so every drill-down adds up to the audited monthly figures. */
export const costRows = (): CostRow[] => {
  if (cache) {
    return cache;
  }
  const rows: CostRow[] = [];
  const random = seeded(42);
  Object.entries(MONTH_TOTALS).forEach(([month, total]) => {
    const [year, mon] = month.split('-').map(Number);
    const days = new Date(Date.UTC(year, mon, 0)).getUTCDate();
    const daily = total / days;
    for (let day = 1; day <= days; day += 1) {
      const date = `${month}-${String(day).padStart(2, '0')}`;
      const weekend = [0, 6].includes(new Date(`${date}T00:00:00Z`).getUTCDay());
      ACCOUNTS.forEach((account) => {
        SERVICES.forEach((service) => {
          // services that only exist in some accounts
          if (service.key === 'AWS Billing Conductor' && account.key !== 'openr (mgmt)') return;
          if (
            service.key === 'Amazon EKS' &&
            !['openr-dev', 'openr-staging', 'openr-prod'].includes(account.key)
          )
            return;
          if (
            service.key === 'Amazon RDS' &&
            !['openr-dev', 'openr-staging', 'openr-prod'].includes(account.key)
          )
            return;
          const jitter = 0.85 + random() * 0.3;
          const load =
            weekend && ['AWS Lambda', 'API Gateway', 'Amazon CloudFront'].includes(service.key)
              ? 0.55
              : 1;
          const base = daily * account.share * service.share * jitter * load;
          service.usage.forEach((usage, index) => {
            const slice = base * (index === 0 ? 0.6 : 0.4 / (service.usage.length - 1 || 1));
            const tagged = service.key === 'Amazon S3' && account.key !== 'openr (mgmt)';
            rows.push({
              date,
              account: account.key,
              service: service.key,
              usage_type: usage,
              tenant: tagged ? TENANTS[Math.floor(random() * TENANTS.length)] : 'untagged',
              region: service.region,
              amount: Math.round(slice * 100) / 100,
            });
          });
        });
      });
    }
  });
  cache = rows;
  return rows;
};

const rangeStart = (range: CostQuery['range'], today: Date): string => {
  const date = new Date(today);
  if (range === '30d') {
    date.setUTCDate(date.getUTCDate() - 29);
  } else {
    const months = Number(range.replace('m', ''));
    date.setUTCMonth(date.getUTCMonth() - months + 1, 1);
  }
  return date.toISOString().slice(0, 10);
};

const previousStart = (start: string, end: string): { from: string; to: string } => {
  const from = new Date(`${start}T00:00:00Z`);
  const to = new Date(`${end}T00:00:00Z`);
  const span = to.getTime() - from.getTime();
  const previousTo = new Date(from.getTime() - 86400000);
  const previousFrom = new Date(previousTo.getTime() - span);
  return {
    from: previousFrom.toISOString().slice(0, 10),
    to: previousTo.toISOString().slice(0, 10),
  };
};

export const runCostQuery = (
  query: CostQuery,
  venues: number,
  today = new Date('2026-10-22T00:00:00Z'),
): CostResult => {
  const rows = costRows();
  const end = today.toISOString().slice(0, 10);
  const start = rangeStart(query.range, today);
  const previous = previousStart(start, end);
  const matches = (row: CostRow) =>
    COST_GROUPINGS.every((dimension) => {
      const wanted = query.filters[dimension];
      return !wanted || wanted.length === 0 || wanted.includes(row[dimension]);
    });
  const inRange = rows.filter((row) => row.date >= start && row.date <= end && matches(row));
  const inPrevious = rows.filter(
    (row) => row.date >= previous.from && row.date <= previous.to && matches(row),
  );
  const bucket = (date: string) => (query.granularity === 'monthly' ? date.slice(0, 7) : date);
  const divisor = query.per_venue && venues > 0 ? venues : 1;

  const dates = Array.from(new Set(inRange.map((row) => bucket(row.date)))).sort();
  const totals = new Map<string, number>();
  const previousTotals = new Map<string, number>();
  const byKeyDate = new Map<string, Map<string, number>>();
  inRange.forEach((row) => {
    const key = row[query.group_by];
    totals.set(key, (totals.get(key) ?? 0) + row.amount);
    const series = byKeyDate.get(key) ?? new Map<string, number>();
    const date = bucket(row.date);
    series.set(date, (series.get(date) ?? 0) + row.amount);
    byKeyDate.set(key, series);
  });
  inPrevious.forEach((row) => {
    const key = row[query.group_by];
    previousTotals.set(key, (previousTotals.get(key) ?? 0) + row.amount);
  });

  const ranked = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
  const top = ranked.slice(0, query.top);
  const rest = ranked.slice(query.top);
  const series: CostSeries[] = top.map(([key, total]) => ({
    key,
    label: key,
    total: total / divisor,
    previous_total: (previousTotals.get(key) ?? 0) / divisor,
    points: dates.map((date) => ({ date, amount: (byKeyDate.get(key)?.get(date) ?? 0) / divisor })),
  }));
  if (rest.length > 0) {
    series.push({
      key: 'other',
      label: `Other (${rest.length})`,
      total: rest.reduce((sum, [, total]) => sum + total, 0) / divisor,
      previous_total:
        rest.reduce((sum, [key]) => sum + (previousTotals.get(key) ?? 0), 0) / divisor,
      points: dates.map((date) => ({
        date,
        amount:
          rest.reduce((sum, [key]) => sum + (byKeyDate.get(key)?.get(date) ?? 0), 0) / divisor,
      })),
    });
  }

  const dimensions = {} as CostResult['dimensions'];
  COST_GROUPINGS.forEach((dimension) => {
    dimensions[dimension] = Array.from(new Set(rows.map((row) => row[dimension])))
      .sort()
      .map((key) => ({ key, label: key }));
  });

  return {
    currency: 'USD',
    granularity: query.granularity,
    dates,
    series,
    total: inRange.reduce((sum, row) => sum + row.amount, 0) / divisor,
    previous_total: inPrevious.reduce((sum, row) => sum + row.amount, 0) / divisor,
    venues,
    dimensions,
  };
};

export interface CostFinding {
  id: string;
  title: string;
  detail: string;
  monthly_usd: number;
  account: string;
  service: string;
}

/** Known waste from the October 2026 infrastructure audit. Will come from the API once it can read Cost Explorer. */
export const COST_FINDINGS: CostFinding[] = [
  {
    id: 'eks-extended',
    title: 'EKS extended support',
    detail:
      'openr-tooling-eks-cluster is on Kubernetes 1.32 and pays $0.60 per cluster hour in extended support.',
    monthly_usd: 934,
    account: 'openr-dev',
    service: 'Amazon EKS',
  },
  {
    id: 'security-vpc',
    title: 'Empty VPC with encryption controls',
    detail:
      'security-agent-vpc has nothing in it but keeps a NAT gateway and VPC encryption controls.',
    monthly_usd: 151,
    account: 'openr-dev',
    service: 'Amazon VPC',
  },
  {
    id: 'tollies-nat',
    title: 'Unused NAT gateway',
    detail: 'tollies-tooling-vpc has a NAT gateway with no traffic.',
    monthly_usd: 36,
    account: 'openr-dev',
    service: 'Amazon EC2',
  },
  {
    id: 'billing-conductor',
    title: 'Billing Conductor',
    detail: 'Pro-forma billing views nobody uses.',
    monthly_usd: 41,
    account: 'openr (mgmt)',
    service: 'AWS Billing Conductor',
  },
  {
    id: 'dev-over-prod',
    title: 'Dev costs more than prod',
    detail:
      'Runners, NAT and Multi-AZ on non-production. Dev has been above prod every month this year.',
    monthly_usd: 400,
    account: 'openr-dev',
    service: 'Amazon RDS',
  },
];
